import { Save } from 'lucide-react';

import { Meter, SaveStatus } from '../../common/index.jsx';

/**
 * The old Final Annotation toolbar: dataset switch, image picker, caption-source
 * switch, review-flag filter, and the two progress readouts.
 *
 * Admins see the real caption source (`claude`, `gemini3.1`, `gpt5.5`) rather than the
 * A/B/C labels annotators get - they are reviewing the pipeline, so which model wrote
 * which caption is the point.
 */

/** Dataset names come from the registry the server sent, never from a map baked in
 *  here - a hardcoded key goes stale the moment a dataset is renamed or removed, and
 *  then shows the raw key to the user. Falls back to the key only if it is unknown. */
export const datasetLabel = (datasets, key) =>
  (datasets || []).find((d) => d.key === key)?.title || key;

/** `claude_opus5` -> `claude`, `gemini3.1_pro` -> `gemini3.1`. As the old tool did. */
export const shortSource = (source) =>
  String(source || '').replace(/_opus5$/, '').replace(/_pro$/, '');

export function AnnotationToolbar({
  index,
  dataset,
  onDataset,
  image,
  onImage,
  sampleId,
  onSample,
  reviewFlag,
  onReviewFlag,
  saveState,
  onRetry,
}) {
  const captions = image?.captions || [];

  return (
    <section className="annotation-toolbar">
      <div className="segmented">
        {(index?.datasets || []).map((d) => (
          <button
            key={d.key}
            type="button"
            aria-pressed={dataset === d.key}
            className={dataset === d.key ? 'active' : ''}
            onClick={() => onDataset(d.key)}
          >
            {d.title}
          </button>
        ))}
      </div>

      <select
        className="select"
        style={{ width: 'auto', minWidth: 240 }}
        value={image?.task_id ?? ''}
        onChange={(e) => onImage(Number(e.target.value))}
        aria-label="Image"
      >
        {(index?.images || []).map((img) => (
          <option key={img.task_id} value={img.task_id}>
            {datasetLabel(index?.datasets, img.dataset_key)} image {img.image_id} ·{' '}
            {img.captions.length} captions
          </option>
        ))}
      </select>

      <div className="caption-source-switch">
        {captions.map((c) => (
          <button
            key={c.sample_id}
            type="button"
            className={c.sample_id === sampleId ? 'active' : ''}
            title={`${c.caption_source}${c.human_review_flag ? ' · flagged for human review' : ''}`}
            onClick={() => onSample(c.sample_id)}
          >
            {c.is_reviewed ? '✓ ' : ''}
            {shortSource(c.caption_source)}
          </button>
        ))}
      </div>

      <select
        className="select"
        style={{ width: 'auto' }}
        value={reviewFlag}
        onChange={(e) => onReviewFlag(e.target.value)}
        aria-label="Review flag filter"
      >
        <option value="all">All review flags</option>
        <option value="needed">Human review needed</option>
        <option value="not_needed">No human review needed</option>
      </select>

      <div className="annotation-progress compact" title="Captions reviewed">
        <strong>
          {index?.reviewed_captions ?? 0}/{index?.total_captions ?? 0}
        </strong>
        <span>reviewed</span>
        <Meter value={index?.reviewed_captions ?? 0} total={index?.total_captions ?? 1} className="sm" />
      </div>

      <div className="annotation-progress compact" title="Images with every caption reviewed">
        <strong>
          {index?.completed_images ?? 0}/{(index?.images || []).length}
        </strong>
        <span>images done</span>
        <Meter
          value={index?.completed_images ?? 0}
          total={(index?.images || []).length || 1}
          className="sm"
        />
      </div>

      <div className="toolbar-save">
        <Save size={14} className="faint" />
        <SaveStatus state={saveState} onRetry={onRetry} />
      </div>
    </section>
  );
}
